import React, { useState, useEffect, useRef } from 'react';
import { Info, Zap, ChevronRight, Sparkles, X } from 'lucide-react';
import { Button, TextField, Toggle, Autocomplete } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import { promptTemplates, libraries } from '../../services/apiService';
import ModalPortal from '../UI/ModalPortal';
import ModalFrame from '../UI/ModalFrame';

const LABEL_CLASS = 'block text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wider mb-1.5';

const TEMPLATE_VARS = [
  { var: '{{talent_name}}', desc: 'Full name' },
  { var: '{{company_name}}', desc: 'Company' },
  { var: '{{company_description}}', desc: 'Company info' },
  { var: '{{professions}}', desc: 'Professions' },
  { var: '{{responsibilities}}', desc: 'Responsibilities' },
  { var: '{{tools}}', desc: 'Skills & tools' },
  { var: '{{languages}}', desc: 'Languages' },
  { var: '{{experience}}', desc: 'CV / experience' },
  { var: '{{media}}', desc: 'Portfolio links' },
];

const buildPromptName = (toolLabel, clientName) => {
  const parts = [toolLabel, clientName].filter(Boolean);
  return parts.join(' – ');
};

const VarChip = ({ variable, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(variable)}
    title="Click to insert"
    className="inline-flex items-center px-2 py-0.5 rounded font-mono text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 hover:bg-indigo-100 dark:hover:bg-indigo-800/40 transition-colors cursor-pointer select-none"
  >
    {variable}
  </button>
);

const PromptTemplateModal = ({ isOpen, onClose, template, onSaved }) => {
  const isEdit = Boolean(template?.id);
  const textareaRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    body: '',
    tool_id: null,
    inner_client_id: null,
    is_default: false,
    is_draft: true
  });
  const [saving, setSaving] = useState(false);
  const [toolOptions, setToolOptions] = useState([]);
  const [innerClientOptions, setInnerClientOptions] = useState([]);
  const [innerClientRaw, setInnerClientRaw] = useState([]);
  const [showVars, setShowVars] = useState(false);
  const nameEditedManually = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    nameEditedManually.current = false;
    if (isEdit && template) {
      setForm({
        name: template.name || '',
        body: template.body || '',
        tool_id: template.tool_id != null ? String(template.tool_id) : null,
        inner_client_id: template.inner_client_id != null ? String(template.inner_client_id) : null,
        is_default: Boolean(template.is_default),
        is_draft: Boolean(template.is_draft)
      });
    } else {
      setForm({ name: '', body: '', tool_id: null, inner_client_id: null, is_default: false, is_draft: true });
    }
    loadOptions();
  }, [isOpen, template]);

  const loadOptions = async () => {
    try {
      const [toolsRes, clientsRes] = await Promise.all([
        libraries.getContactTools().catch(() => ({ data: [] })),
        libraries.getInnerClients().catch(() => ({ data: [] }))
      ]);
      const tools = Array.isArray(toolsRes?.data) ? toolsRes.data : (Array.isArray(toolsRes) ? toolsRes : []);
      setToolOptions(tools.map(t => ({ value: String(t.id), label: t.name || t.title })));
      const clients = Array.isArray(clientsRes?.data) ? clientsRes.data : [];
      setInnerClientRaw(clients);
      setInnerClientOptions(clients.map(c => ({ value: String(c.id), label: c.name })));
    } catch (err) {
      console.error('Failed to load options:', err);
    }
  };

  const autoUpdateName = (toolId, innerClientId) => {
    if (nameEditedManually.current) return null;
    const hasCriteria = toolId || innerClientId;
    if (!hasCriteria) return null;
    const tool = toolId ? toolOptions.find(t => Number(t.value) === Number(toolId)) : null;
    const client = innerClientId ? innerClientRaw.find(c => Number(c.id) === Number(innerClientId)) : null;
    return buildPromptName(tool?.label, client?.name);
  };

  const insertVar = (variable) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newBody = form.body.slice(0, start) + variable + form.body.slice(end);
    setForm(prev => ({ ...prev, body: newBody }));
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + variable.length, start + variable.length);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.body.trim()) { toast.error('Prompt body is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        body: form.body.trim(),
        tool_id: form.tool_id ? parseInt(form.tool_id) : null,
        inner_client_id: form.inner_client_id ? parseInt(form.inner_client_id) : null,
        is_default: form.is_default,
        is_draft: form.is_draft
      };
      if (isEdit) {
        await promptTemplates.update(template.id, payload);
        toast.success('Prompt template updated');
      } else {
        await promptTemplates.create(payload);
        toast.success('Prompt template created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const bodyCharCount = form.body.length;

  if (!isOpen) return null;

  return (
    <ModalPortal onClose={saving ? undefined : onClose} ariaLabel="Prompt template editor">
      <ModalFrame size="xl" className="-m-3 w-[calc(100%+1.5rem)] rounded-none sm:m-0 sm:w-full sm:rounded-lg">
        <form onSubmit={handleSubmit} className="contents">
        {/* Header */}
        <ModalFrame.Header className="items-center px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shrink-0">
              <Sparkles size={15} className="text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">
                {isEdit ? 'Edit Prompt' : 'New Prompt'}
              </h2>
              <p className="truncate text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                AI instructions for post generation
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} aria-label="Close" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-light-text-tertiary hover:bg-light-bg-secondary dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary disabled:opacity-50">
            <X className="h-5 w-5" />
          </button>
        </ModalFrame.Header>

        {/* Two-column layout */}
        <ModalFrame.Body className="p-0">
        <div className="flex min-w-0 flex-col lg:flex-row">
          {/* LEFT — meta & settings */}
          <div className="flex w-full shrink-0 flex-col border-b border-light-border-primary dark:border-dark-border-primary lg:w-72 lg:border-b-0 lg:border-r">
            <div className="p-5 space-y-5 flex-1">

              {/* Name */}
              <TextField
                label="Name"
                required
                size="small"
                fullWidth
                value={form.name}
                onChange={e => {
                  nameEditedManually.current = true;
                  setForm(prev => ({ ...prev, name: e.target.value }));
                }}
                placeholder="e.g. LinkedIn EN"
              />

              {/* Separator */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-light-border-primary dark:bg-dark-border-primary" />
                <div className="flex items-center gap-1.5 text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                  <Zap size={11} />
                  <span>Auto-match</span>
                </div>
                <div className="flex-1 h-px bg-light-border-primary dark:bg-dark-border-primary" />
              </div>

              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary -mt-2 leading-relaxed">
                Assigned automatically to Talent Posts matching <em>all</em> criteria. More specific = higher priority.
              </p>

              {/* Platform */}
              <div>
                <label className={LABEL_CLASS}>Platform</label>
                <Autocomplete
                  clearable
                  fullWidth
                  options={toolOptions}
                  value={form.tool_id}
                  onChange={val => {
                    const newName = autoUpdateName(val, form.inner_client_id);
                    setForm(prev => ({ ...prev, tool_id: val || null, ...(newName !== null ? { name: newName } : {}) }));
                  }}
                  placeholder="Any platform..."
                />
              </div>

              {/* Inner Client */}
              <div>
                <label className={LABEL_CLASS}>Inner Client</label>
                <Autocomplete
                  clearable
                  fullWidth
                  options={innerClientOptions}
                  value={form.inner_client_id}
                  onChange={val => {
                    const newName = autoUpdateName(form.tool_id, val);
                    setForm(prev => ({ ...prev, inner_client_id: val || null, ...(newName !== null ? { name: newName } : {}) }));
                  }}
                  placeholder="Any client..."
                />
              </div>

              {/* Is Default */}
              <Toggle
                checked={form.is_default}
                onChange={(checked) => setForm(prev => ({ ...prev, is_default: checked }))}
                label="Mark as default"
                color="primary"
              />

              {/* Is Draft */}
              <Toggle
                checked={form.is_draft}
                onChange={(checked) => setForm(prev => ({ ...prev, is_draft: checked }))}
                label="Save as draft"
                color="warning"
              />
            </div>

          </div>

          {/* RIGHT — editor */}
          <div className="flex min-w-0 flex-1 flex-col">
            {/* Editor toolbar */}
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-light-border-primary px-4 py-2.5 dark:border-dark-border-primary sm:px-5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wider">
                  Prompt Body
                </span>
                <span className="text-red-400 text-xs">*</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary tabular-nums">
                  {bodyCharCount.toLocaleString()} chars
                </span>
                <button
                  type="button"
                  onClick={() => setShowVars(v => !v)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    showVars
                      ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                      : 'text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-tertiary'
                  }`}
                >
                  <Info size={12} />
                  Variables
                  <ChevronRight size={12} className={`transition-transform ${showVars ? 'rotate-90' : ''}`} />
                </button>
              </div>
            </div>

            {/* Variables panel (collapsible) */}
            {showVars && (
              <div className="px-5 py-3 border-b border-light-border-primary dark:border-dark-border-primary bg-indigo-50/50 dark:bg-indigo-900/10 shrink-0">
                <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-2">
                  Click a variable to insert it at cursor position:
                </p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {TEMPLATE_VARS.map(({ var: v, desc }) => (
                    <span key={v} title={desc}>
                      <VarChip variable={v} onClick={insertVar} />
                    </span>
                  ))}
                </div>
                <div className="rounded-lg bg-indigo-100/70 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700/50 px-3 py-2 space-y-1">
                  <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">How variables affect generation</p>
                  <p className="text-xs text-indigo-600/80 dark:text-indigo-400/80 leading-relaxed">
                    <strong>With variables</strong> — data is substituted directly into your prompt text. No extra structured sections are appended — you fully control what the AI sees.
                  </p>
                  <p className="text-xs text-indigo-600/80 dark:text-indigo-400/80 leading-relaxed">
                    <strong>Without variables</strong> — full talent data (name, professions, skills, languages, experience) is automatically appended as structured sections after your prompt.
                  </p>
                </div>
              </div>
            )}

            {/* Textarea */}
            <div className="flex min-h-[22rem] flex-1 flex-col p-4">
              <TextField
                ref={textareaRef}
                multiline
                fullWidth
                value={form.body}
                onChange={e => setForm(prev => ({ ...prev, body: e.target.value }))}
                placeholder={"Write your AI instructions here…\n\nExample:\nYou are a professional copywriter. Create an engaging LinkedIn post for {{talent_name}}, highlighting their expertise in {{professions}}."}
                spellCheck={false}
                sx={{
                  root: 'flex-1 flex flex-col min-h-0',
                  wrapper: 'flex-1 flex',
                  input: 'flex-1 font-mono text-sm leading-relaxed resize-none',
                }}
              />
            </div>
          </div>
        </div>
        </ModalFrame.Body>
        <ModalFrame.Footer className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&>*]:w-full sm:[&>*]:w-auto">
          <Button type="button" variant="text" color="neutral" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Prompt'}
          </Button>
        </ModalFrame.Footer>
        </form>
      </ModalFrame>
    </ModalPortal>
  );
};

export default PromptTemplateModal;
